import React, { createContext, useContext, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Grid } from '@mui/material';
import { usePostHog } from 'posthog-js/react';
import TagManager from 'react-gtm-module';
import * as Sentry from '@sentry/react';
import { AccountData, AccountService } from '../../api';
import { WidgetThemeProvider } from '../theme/Theme';
import { monkeybookStandardTheme } from '../theme/monkeybookTheme';
import RotatingLoader from '../../ui/RotatingLoader';
import { LoaderLogoProvider } from '../../ui/LoaderLogo';
import { deepOmitNull } from '../utils';
import { useLanguageChange } from '../hooks/useLanguageChange';
import { setUTMData } from '../orderCompletion/utmLocalStorage';
import ErrorBoundary from '../../ErrorBoundery';
import { safeStartSessionRecording } from '../../utils/safePosthog';

const AccountDataContext = createContext<AccountData | undefined>(undefined);

export const useAccountData = () => {
  return useContext(AccountDataContext);
};

export const AccountSpecificThemeWrapper = ({
  children,
  useMonkeybookTheme,
}: {
  children: React.ReactNode;
  useMonkeybookTheme?: boolean;
}) => {
  const { accountId } = useParams();
  const [searchParams] = useSearchParams();
  // order completed page has account in query params
  const qAccountId = searchParams.get('account_id') || undefined;
  const postHog = usePostHog();

  const [account, setAccount] = useState<AccountData | undefined>(undefined);
  const { isLoading: isLoadingLanguage } = useLanguageChange(account?.default_system_language);
  useEffect(() => {
    safeStartSessionRecording(postHog);
  }, [postHog]);
  useEffect(() => {
    if (account) {
      postHog.setPersonPropertiesForFlags({ accountId: account.id });
      postHog.setPersonProperties({
        accountId: account.id,
        accountName: account.name,
        environment: import.meta.env.VITE_REACT_APP_ENVIRONMENT || 'local',
        context: 'accountSpecificThemeWrapper',
      });
    }
  }, [account]);
  useEffect(() => {
    const id = accountId || qAccountId;
    if (!id) {
      Sentry.captureMessage('AccountSpecificThemeWrapper: missing accountId', {
        level: 'warning',
        extra: {
          pathname: window.location.pathname,
          search: window.location.search,
          href: window.location.href,
          referrer: document.referrer,
        },
      });
      setUTMData();
      return;
    }
    void (async () => {
      const accountData = (await AccountService.getAccount(id)) as AccountData;
      setAccount(accountData);
      if (accountData?.marketing_integrations?.gtm_id) {
        TagManager.initialize({ gtmId: accountData.marketing_integrations.gtm_id });
      }
    })();
    setUTMData();
  }, [accountId, qAccountId]);

  const hasAccountId = Boolean(accountId || qAccountId);
  if (hasAccountId && (!account || isLoadingLanguage)) {
    return (
      <Grid container justifyContent="space-around" mt={3}>
        <Grid
          container
          minHeight={336}
          sx={{
            borderRadius: '12px',
            width: '322px',
          }}>
          <RotatingLoader />
        </Grid>
      </Grid>
    );
  }

  const accountTheme = account ? deepOmitNull(account.custom_theme) : undefined;
  const externalTheme = useMonkeybookTheme ? monkeybookStandardTheme : accountTheme;

  return (
    <ErrorBoundary>
      <AccountDataContext.Provider value={account}>
        <LoaderLogoProvider account={account}>
          {/* @ts-ignore */}
          <WidgetThemeProvider remoteThemeOverride={externalTheme}>{children}</WidgetThemeProvider>
        </LoaderLogoProvider>
      </AccountDataContext.Provider>
    </ErrorBoundary>
  );
};
